import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import MemberRoleSelect from './MemberRoleSelect';
import DeleteMemberButton from './DeleteMemberButton';
import AddMemberButton from './AddMemberButton';

export default function Members(props) {
  const {
    organizationId, members, setMembers, pendingMembers, setpendingMembers,
  } = props;
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'invited', 'pending'

  const activeMembers = members.filter((m) => m.membershipState === 'accepted');
  const activeMemberEmails = activeMembers.map((m) => m.email);
  const invitedMembers = members.filter((m) => m.membershipState === 'invited'
    && !activeMemberEmails.includes(m.email));

  // Combine all members with their status
  const allMembers = [
    ...activeMembers.map((m) => ({ ...m, status: 'active', userExists: true })),
    ...invitedMembers.map((m) => ({ ...m, status: 'invited', userExists: true })),
    ...pendingMembers.map((m) => ({ ...m, status: 'pending', userExists: false })),
  ];

  // Filter members based on selected filter
  const filteredMembers = filter === 'all'
    ? allMembers
    : allMembers.filter((m) => m.status === filter);

  const handleDelete = (memberId) => {
    // Check if the memberId exists in the members array
    const memberExists = members.some((m) => m.id === memberId);
    if (memberExists) {
      const newMembers = members.filter((m) => m.id !== memberId);
      setMembers(newMembers);
    } else {
      const newPendingMembers = pendingMembers.filter((m) => m.id !== memberId);
      setpendingMembers(newPendingMembers);
    }
  };

  const handleAdded = (member) => {
    if (member.userExists) {
      const newMembers = [...members];
      newMembers.push(member);
      setMembers(newMembers);
    } else {
      const newMembers = [...pendingMembers];
      newMembers.push(member);
      setpendingMembers(newMembers);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: t('organizations.active_members'), color: 'success' },
      invited: { label: t('organizations.invited_members'), color: 'warning' },
      pending: { label: t('organizations.pending_members'), color: 'info' },
    };

    const config = statusConfig[status] || statusConfig.active;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const getRoleChip = (role) => {
    const roleConfig = {
      admin: { label: t('member_role.admin'), color: 'error' },
      member: { label: t('member_role.member'), color: 'primary' },
    };

    const config = roleConfig[role] || roleConfig.member;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="filled"
      />
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 3,
        width: '100%',
      }}
    >
      {/* Header with Add Member Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {t('organizations.members')}
        </Typography>
        <AddMemberButton organizationId={organizationId} onAdded={handleAdded} />
      </Box>

      {/* Filter Chips */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: t('all') },
          { key: 'active', label: t('organizations.active_members') },
          { key: 'invited', label: t('organizations.invited_members') },
          { key: 'pending', label: t('organizations.pending_members') },
        ].map((filterOption) => (
          <Chip
            key={filterOption.key}
            label={filterOption.label}
            onClick={() => setFilter(filterOption.key)}
            color={filter === filterOption.key ? 'primary' : 'default'}
            variant={filter === filterOption.key ? 'filled' : 'outlined'}
            clickable
          />
        ))}
      </Box>

      {/* Members Table */}
      <TableContainer component={Paper} sx={{ width: '100%' }}>
        <Table sx={{ minWidth: 650 }} aria-label="members table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('organizations.membership_state')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('organizations.role')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('organizations.no_members_found', 'No members found')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredMembers.map((member) => (
                <TableRow
                  key={member.id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    {getStatusChip(member.status)}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'medium',
                        wordBreak: 'break-all',
                      }}
                    >
                      {member.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <MemberRoleSelect
                      userExists={member.userExists}
                      defaultValue={member.role}
                      organizationId={organizationId}
                      userId={member.id}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <DeleteMemberButton
                      onDeleted={handleDelete}
                      memberId={member.id}
                      organizationId={organizationId}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {t('organizations.total_members', 'Total: {{count}} members', {
            count: allMembers.length,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('organizations.active_count', 'Active: {{count}}', {
            count: activeMembers.length,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('organizations.invited_count', 'Invited: {{count}}', {
            count: invitedMembers.length,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('organizations.pending_count', 'Pending: {{count}}', {
            count: pendingMembers.length,
          })}
        </Typography>
      </Box>
    </Box>
  );
}
